import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, CircularProgress, MenuItem, TextField } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import DevopsRecordShell from "../components/devops/DevopsRecordShell";
import { devopsService } from "../api/services/devopsService";
import { useAuth } from "../contexts/AuthContext";
import { useDevopsWorkspace } from "../contexts/DevopsWorkspaceContext";
import { EntityStatus } from "../types";
import type {
  ConfigurationFormat,
  ConfigurationScope,
  DevopsApplication,
  DevopsConfiguration,
  DevopsEnvironment,
} from "../types/devops";
import { canMaintainConfiguration } from "../utils/devopsAccess";
import { isDevopsOperator, isEnvironmentManager } from "../utils/appRoles";
import { devopsRoutes, type DevopsRecordMode } from "../utils/devopsRoutes";
import { getTenantId } from "../utils/jwtUtils";

interface ConfigurationRecordPageProps {
  mode: DevopsRecordMode;
}

const ConfigurationRecordPage: React.FC<ConfigurationRecordPageProps> = ({ mode }) => {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { bindEntity } = useDevopsWorkspace();
  const roles = user?.roles ?? [];
  const canEnv = isEnvironmentManager(roles);
  const canApp = isDevopsOperator(roles);
  const defaultScope: ConfigurationScope = canEnv && !canApp ? "ENVIRONMENT" : "APPLICATION";
  const [item, setItem] = useState<DevopsConfiguration | null>(null);
  const [environments, setEnvironments] = useState<DevopsEnvironment[]>([]);
  const [applications, setApplications] = useState<DevopsApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [scope, setScope] = useState<ConfigurationScope>(defaultScope);
  const [environmentUuid, setEnvironmentUuid] = useState("");
  const [format, setFormat] = useState<ConfigurationFormat>("YAML");
  const [content, setContent] = useState("");
  const [owner, setOwner] = useState(user?.email ?? "");
  const [applicationUuids, setApplicationUuids] = useState<string[]>([]);
  const [status, setStatus] = useState<EntityStatus>(EntityStatus.ACTIVE);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canWrite = item
    ? canMaintainConfiguration(roles, item.scope)
    : canEnv || canApp;
  const readOnly = mode === "view";

  useEffect(() => {
    if (mode === "edit" && item && !canMaintainConfiguration(roles, item.scope) && uuid) {
      navigate(devopsRoutes.configurationView(uuid), { replace: true });
    }
  }, [mode, item, roles, uuid, navigate]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [envs, apps, cfg] = await Promise.all([
          devopsService.listEnvironments(),
          devopsService.listApplications(),
          mode === "create" || !uuid ? Promise.resolve(null) : devopsService.getConfiguration(uuid),
        ]);
        if (cancelled) return;
        setEnvironments(envs);
        setApplications(apps);
        if (mode !== "create") {
          if (!cfg) {
            setError("Configuration not found.");
            return;
          }
          setItem(cfg);
          setName(cfg.name);
          setScope(cfg.scope);
          setEnvironmentUuid(cfg.environment.uuid);
          setFormat(cfg.format);
          setContent(cfg.content ?? "");
          setOwner(cfg.owner);
          setApplicationUuids((cfg.applications ?? []).map((app) => app.uuid));
          setStatus(cfg.status);
          bindEntity({ type: "CONFIGURATION", uuid: cfg.uuid, name: cfg.name });
        } else {
          setEnvironmentUuid(envs[0]?.uuid ?? "");
          setScope(defaultScope);
        }
      } catch (err: unknown) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load configuration");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [mode, uuid, bindEntity, defaultScope]);

  const mappedApps = useMemo(() => {
    const env = environments.find((e) => e.uuid === environmentUuid);
    const mappedIds = new Set((env?.applications ?? []).map((app) => app.uuid));
    return applications.filter((app) => mappedIds.has(app.uuid));
  }, [applications, environments, environmentUuid]);

  const scopeOptions: ConfigurationScope[] = [
    ...(canEnv ? (["ENVIRONMENT"] as const) : []),
    ...(canApp ? (["APPLICATION"] as const) : []),
  ];

  const handleSave = async () => {
    const tenantId = getTenantId();
    if (!tenantId) {
      setError("No tenant on the current session.");
      return;
    }
    if (!name.trim() || !owner.trim() || !environmentUuid) {
      setError("Name, owner, and environment are required.");
      return;
    }
    if (scope === "APPLICATION" && applicationUuids.length === 0 && !item) {
      setError("App-level configuration must link at least one application in this environment.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const saved = item
        ? await devopsService.updateConfiguration(item.uuid, {
            name: name.trim(),
            content,
            status,
          })
        : await devopsService.createConfiguration({
            tenantId,
            name: name.trim(),
            status,
            scope,
            environmentUuid,
            format,
            content,
            owner: owner.trim(),
            applicationUuids: scope === "APPLICATION" ? applicationUuids : [],
          });
      navigate(devopsRoutes.configurationView(saved.uuid));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  const title =
    mode === "create"
      ? "Add configuration"
      : mode === "edit"
        ? `Edit ${item?.name ?? "configuration"}`
        : item?.name ?? "Configuration";

  if (loading) return <CircularProgress size={24} />;

  return (
    <DevopsRecordShell
      title={title}
      backPath={mode === "edit" && uuid ? devopsRoutes.configurationView(uuid) : devopsRoutes.configurations}
      backLabel={mode === "edit" ? "Back to view" : "Back to list"}
      actions={
        mode === "view" && canWrite && uuid ? (
          <Button variant="contained" onClick={() => navigate(devopsRoutes.configurationEdit(uuid))}>
            Edit
          </Button>
        ) : undefined
      }
    >
      {error && <Alert severity="error">{error}</Alert>}
      <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} required disabled={readOnly} />
      <TextField
        select
        label="Scope"
        value={scope}
        onChange={(e) => setScope(e.target.value as ConfigurationScope)}
        disabled={readOnly || Boolean(item) || scopeOptions.length < 2}
      >
        {(item ? [item.scope] : scopeOptions).map((value) => (
          <MenuItem key={value} value={value}>
            {value}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        label="Environment"
        value={environmentUuid}
        onChange={(e) => setEnvironmentUuid(e.target.value)}
        disabled={readOnly || Boolean(item)}
      >
        {environments.map((env) => (
          <MenuItem key={env.uuid} value={env.uuid}>
            {env.name}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        label="Format"
        value={format}
        onChange={(e) => setFormat(e.target.value as ConfigurationFormat)}
        disabled={readOnly || Boolean(item)}
      >
        <MenuItem value="YAML">YAML</MenuItem>
        <MenuItem value="PROPERTIES">PROPERTIES</MenuItem>
      </TextField>
      {(scope === "APPLICATION" && (!item || readOnly)) && (
        <TextField
          select
          SelectProps={{ multiple: true }}
          label="Applications"
          value={applicationUuids}
          onChange={(e) =>
            setApplicationUuids(
              typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value,
            )
          }
          helperText="Must already be mapped into the selected environment."
          disabled={readOnly || Boolean(item)}
        >
          {(readOnly ? applications.filter((app) => applicationUuids.includes(app.uuid)) : mappedApps).map((app) => (
            <MenuItem key={app.uuid} value={app.uuid}>
              {app.name}
            </MenuItem>
          ))}
        </TextField>
      )}
      <TextField label="Owner" value={owner} onChange={(e) => setOwner(e.target.value)} required disabled={readOnly || Boolean(item)} />
      <TextField
        select
        label="Status"
        value={status}
        onChange={(e) => setStatus(e.target.value as EntityStatus)}
        disabled={readOnly}
      >
        {Object.values(EntityStatus).map((value) => (
          <MenuItem key={value} value={value}>
            {value}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label="Content"
        value={content}
        onChange={(e) => setContent(e.target.value)}
        multiline
        minRows={8}
        helperText="Secret names and refs only — never paste secret values."
        disabled={readOnly}
      />
      {!readOnly && (
        <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ alignSelf: "flex-start" }}>
          Save
        </Button>
      )}
    </DevopsRecordShell>
  );
};

export default ConfigurationRecordPage;
